'use client'

import { Volume2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import InlineAudioPlayer from '@/components/markdown/inline-audio-player'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface AudioPhraseSpanProps {
  audioUrl: string
  hint?: string
  children: React.ReactNode
  className?: string
}

export default function AudioPhraseSpan({ audioUrl, hint, children, className }: AudioPhraseSpanProps) {
  const [open, setOpen] = React.useState(false)

  const phraseText = React.useMemo(() => {
    if (typeof children === 'string') return children
    return React.Children.toArray(children).join('')
  }, [children])

  if (!audioUrl) {
    return <span className={className}>{children}</span>
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          className={cn(
            'inline cursor-pointer rounded-sm border-b-2 border-primary/30 bg-primary/[0.06] px-0.5 font-semibold text-primary transition hover:border-primary/60 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            className
          )}
        >
          {children}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3 p-4">
        {open ? <InlineAudioPlayer audioUrl={audioUrl} variant="popover" /> : null}
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <p className="flex-1 text-sm font-semibold capitalize leading-snug">{phraseText}</p>
            <Volume2 className="mt-0.5 size-4 shrink-0 text-primary/60" aria-hidden />
          </div>
          {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
